import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { UserIcon, ScaleIcon, ActivityIcon, ShieldAlertIcon, LockIcon, HeartHandshakeIcon, ClipboardCheckIcon, StethoscopeIcon } from 'lucide-react';
export function AboutPage() {
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const fadeUpItem = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <div className="w-full bg-brandbg" data-id="element-151">
      {/* Intro Section */}
      <section className="py-16 md:py-24 bg-white border-b border-bordercolor" data-id="element-152">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-153">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="text-3xl md:text-5xl font-bold text-brandtext mb-6" data-id="element-154">
            About <span className="text-primary" data-id="element-155">[CLINIC NAME]</span>
          </motion.h1>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="space-y-6 text-lg text-brandtext-muted leading-relaxed" data-id="element-156">
            <p data-id="element-157">
              We are a dedicated medical clinic specializing in evidence-based
              Testosterone Replacement Therapy (TRT). Our mission is to provide
              safe, highly monitored, and medically sound hormone optimization
              through the convenience of modern telehealth.
            </p>
            <p data-id="element-158">
              Unlike generic wellness brands, we operate strictly within
              established clinical guidelines. We believe that hormone therapy
              requires rigorous diagnostic evaluation, personalized dosing, and
              continuous laboratory monitoring to ensure both efficacy and
              long-term patient safety.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Provider Cards */}
      <section className="py-20" data-id="element-159">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-160">
          <div className="text-center mb-12" data-id="element-161">
            <h2 className="text-2xl font-bold text-brandtext" data-id="element-162">
              Our Clinical Team
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto mt-4 rounded-full" data-id="element-163"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-164">
            {[1, 2, 3].map(provider => <div key={provider} className="bg-surface border border-bordercolor rounded-xl p-6 shadow-subtle flex flex-col items-center text-center" data-id="element-165">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-5 border-4 border-white shadow-sm" data-id="element-166">
                  <UserIcon className="w-10 h-10 text-gray-400" data-id="element-167" />
                </div>
                <h3 className="text-lg font-bold text-brandtext" data-id="element-168">
                  [Provider Name]
                </h3>
                <p className="text-primary font-medium text-sm mb-4" data-id="element-169">
                  [Credentials, e.g., MD, Board Certified]
                </p>
                <p className="text-sm text-brandtext-muted leading-relaxed" data-id="element-170">
                  [Placeholder bio: A brief description of the provider's
                  medical background, specialization in endocrinology or men's
                  health, and commitment to patient care.]
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Standards of Care */}
      <section className="py-20 bg-white border-y border-bordercolor" data-id="element-171">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-172">
          <div className="mb-16" data-id="element-173">
            <h2 className="text-3xl font-bold text-brandtext mb-4" data-id="element-174">
              Our Standards of Care
            </h2>
            <p className="text-brandtext-muted text-lg max-w-2xl" data-id="element-175">
              We adhere to strict clinical protocols to ensure your treatment is
              both safe and effective.
            </p>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-176">
            {[{
            title: 'Guideline-based dosing',
            icon: <ScaleIcon className="w-6 h-6" data-id="element-177" />,
            desc: 'Prescriptions are strictly based on clinical guidelines, never exceeding safe physiological ranges.'
          }, {
            title: 'Regular lab monitoring',
            icon: <ActivityIcon className="w-6 h-6" data-id="element-178" />,
            desc: 'Mandatory blood panels to track hormone levels, hematocrit, and overall metabolic health.'
          }, {
            title: 'Risk/benefit review',
            icon: <ShieldAlertIcon className="w-6 h-6" data-id="element-179" />,
            desc: 'Continuous evaluation of treatment efficacy versus potential side effects.'
          }, {
            title: 'Secure technology',
            icon: <LockIcon className="w-6 h-6" data-id="element-180" />,
            desc: 'All consultations and records are maintained on encrypted, HIPAA-compliant platforms.'
          }].map((item, i) => <motion.div key={i} variants={fadeUpItem} className="flex flex-col" data-id="element-181">
                <div className="w-12 h-12 bg-primary-light rounded-lg flex items-center justify-center text-primary mb-4" data-id="element-182">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold text-brandtext mb-2" data-id="element-183">
                  {item.title}
                </h3>
                <p className="text-sm text-brandtext-muted leading-relaxed" data-id="element-184">
                  {item.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Treatments Section */}
      <section className="py-20" data-id="element-185">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-186">
          <div className="text-center mb-12" data-id="element-187">
            <h2 className="text-2xl font-bold text-brandtext" data-id="element-188">
              Clinical Services
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-189">
            <div className="bg-surface border border-bordercolor p-6 rounded-lg shadow-subtle" data-id="element-190">
              <ClipboardCheckIcon className="w-8 h-8 text-primary mb-4" data-id="element-191" />
              <h3 className="font-semibold text-brandtext mb-2" data-id="element-192">
                Comprehensive Evaluation
              </h3>
              <p className="text-sm text-brandtext-muted" data-id="element-193">
                In-depth clinical assessment of symptoms, medical history, and
                diagnostic laboratory testing to determine eligibility.
              </p>
            </div>
            <div className="bg-surface border border-bordercolor p-6 rounded-lg shadow-subtle" data-id="element-194">
              <StethoscopeIcon className="w-8 h-8 text-primary mb-4" data-id="element-195" />
              <h3 className="font-semibold text-brandtext mb-2" data-id="element-196">
                Lab Review & Planning
              </h3>
              <p className="text-sm text-brandtext-muted" data-id="element-197">
                Detailed provider review of blood panels to create a customized,
                medically appropriate hormone optimization plan.
              </p>
            </div>
            <div className="bg-surface border border-bordercolor p-6 rounded-lg shadow-subtle" data-id="element-198">
              <HeartHandshakeIcon className="w-8 h-8 text-primary mb-4" data-id="element-199" />
              <h3 className="font-semibold text-brandtext mb-2" data-id="element-200">
                Monitoring & Follow-up
              </h3>
              <p className="text-sm text-brandtext-muted" data-id="element-201">
                Ongoing telehealth consultations and periodic lab work to ensure
                treatment remains safe and effective over time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Block */}
      <section className="pb-20 pt-4" data-id="element-202">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-203">
          <div className="bg-secondary/30 border border-secondary rounded-xl p-8 text-center" data-id="element-204">
            <h3 className="text-lg font-bold text-brandtext mb-4" data-id="element-205">
              Scope of Practice & Compliance
            </h3>
            <div className="space-y-3 text-sm text-brandtext-muted" data-id="element-206">
              <p data-id="element-207">
                <strong data-id="element-208">Notice:</strong> Telehealth services are currently only
                available to eligible patients residing in{' '}
                <strong data-id="element-209">[STATE]</strong>.
              </p>
              <p className="italic" data-id="element-210">[STATE LICENSE TEXT]</p>
              <p data-id="element-211">
                Final treatment decisions, including the issuance of any
                prescriptions, are made exclusively by licensed medical
                providers after a comprehensive clinical evaluation. Not all
                patients will qualify for treatment.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}